/**
 * Shared shape + unwrapper for PLP fetcher responses.
 *
 * The catch-all dispatcher (`routes/$.tsx`) wraps category-PLP loader
 * output as `{ type: "plp", data: payload }`, while `/search` returns
 * `payload` directly. Anything that calls `useFetcher` against a PLP
 * loader has to handle both shapes; centralising the unwrap here keeps
 * the dispatcher's envelope knowledge in one place so future changes to
 * it can't drift away from the consumers.
 *
 * `TData` is the resolved hits-payload type of whichever loader the
 * caller targets (e.g. `ResolvedCategoryData` in `CategoryGridBody`).
 */
import { z } from "zod";

export type PlpFetcherPayload<TData> = {
  data?: TData | Promise<TData>;
  $page?: number | "all";
};

/**
 * SFCC-11462 — error-as-data sentinel for a paginated / fan-out listing
 * failure. The loaders return this in the `data` field instead of throwing;
 * `usePaginationFetcher` detects it, skips the grid merge, and the route
 * toasts.
 *
 * Validated with Zod rather than an `instanceof` check or a stringly-typed
 * `"key" in obj` test: the sentinel is serialized across the SSR→client
 * boundary inside `fetcher.data`, so any class identity is lost on the client
 * (same reason a custom `Error` subclass can't be used). A Zod schema is the
 * single source of truth for the shape and validates a plain object on either
 * side — mirroring `throwIfScapiError`'s Zod-based error-envelope detection.
 * Lives here (non-`.server`, already shared) so both loaders and the client
 * fetcher import the same schema/guard.
 */
export const PlpPaginationErrorSchema = z.object({
  paginationFailed: z.literal(true),
});

export type PlpPaginationError = z.infer<typeof PlpPaginationErrorSchema>;

export const isPlpPaginationError = (
  value: unknown,
): value is PlpPaginationError =>
  PlpPaginationErrorSchema.safeParse(value).success;

export type DispatcherPlpResponse<TData> =
  | (PlpFetcherPayload<TData> & { type?: undefined })
  | { type: "plp"; data: PlpFetcherPayload<TData> };

export function unwrapPlpPayload<TData>(
  resp: DispatcherPlpResponse<TData> | undefined,
): PlpFetcherPayload<TData> {
  if (!resp) return {};
  if ("type" in resp && resp.type === "plp") return resp.data;
  return resp;
}
